import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Timeline } from "@/components/Timeline";
import { PestCard } from "@/components/PestCard";
import { DiseaseCard } from "@/components/DiseaseCard";
import { DashboardBanners } from "@/components/DashboardBanners";
import { FruitProfile } from "@/components/FruitProfile";
import { hasFruitProfile } from "@/lib/plans";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Droplets, Leaf, Upload, Trash2, Camera, ArrowLeft, Check, Sprout, Mountain, ShieldAlert, AlertTriangle, CloudRain, Sparkles } from "lucide-react";

export default function TreeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, preview } = useAuth();
  const canAI = user?.role === "provider" || user?.plan === "Orchardist";
  const [tree, setTree] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [imgData, setImgData] = useState("");
  const [busy, setBusy] = useState(false);
  const [diagnosing, setDiagnosing] = useState([]);
  const [rain, setRain] = useState(null);
  const [loc, setLoc] = useState({ latitude: "", longitude: "" });
  const [editLoc, setEditLoc] = useState(false);
  const fileRef = useRef();

  const checkRain = () => api.get(`/trees/${id}/rain-check`).then((r) => setRain(r.data)).catch(() => setRain(null));

  const load = () => api.get(`/trees/${id}`).then((r) => {
    setTree(r.data);
    setLoc({ latitude: r.data.latitude ?? "", longitude: r.data.longitude ?? "" });
    setSelected((s) => {
      const fresh = s && r.data.milestones.find((m) => m.key === s.key);
      return fresh || r.data.milestones.find((m) => m.status === "active") || r.data.milestones[0];
    });
    checkRain();
  });
  useEffect(() => { load(); }, [id]);

  const saveLoc = async () => {
    if (loc.latitude === "" || loc.longitude === "") { toast.error("Enter both latitude and longitude"); return; }
    try {
      await api.put(`/trees/${id}/location`, { latitude: Number(loc.latitude), longitude: Number(loc.longitude) });
      toast.success("Location saved · checking rainfall");
      setEditLoc(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setImgData(reader.result);
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!imgData) { toast.error("Choose a photo first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leaf-photos", { tree_id: id, image: imgData, note });
      toast.success("Photo sent · running AI diagnosis…");
      setImgData(""); setNote(""); if (fileRef.current) fileRef.current.value = "";
      await load();
      if (canAI) runDiagnose(data.id);
      else toast.message("Upgrade to Orchardist to unlock AI leaf diagnosis");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runDiagnose = async (pid) => {
    setDiagnosing((s) => [...s, pid]);
    try {
      await api.post(`/leaf-photos/${pid}/diagnose`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Diagnosis failed"); }
    finally { setDiagnosing((s) => s.filter((x) => x !== pid)); }
  };

  const photoSrc = (p) => {
    const u = p.image_url || p.image;
    if (!u) return "";
    if (u.startsWith("data:")) return u;
    return `${API}${u.replace(/^\/api/, "")}?auth=${localStorage.getItem("ag_token")}`;
  };

  const remove = async () => {
    await api.delete(`/trees/${id}`);
    toast.success("Tree profile removed");
    navigate("/dashboard");
  };

  const setZone = async (zone) => {
    try {
      await api.put(`/trees/${id}/climate-zone`, { climate_zone: zone });
      toast.success(`Climate zone updated · ${zone}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setUnits = async (units) => {
    try {
      await api.put(`/trees/${id}/units`, { units });
      toast.success(`Units updated · ${units === "imperial" ? "US / Imperial" : "Metric"}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!tree) return <div className="min-h-screen"><Navbar /><p className="p-10 font-serif text-2xl text-stone-400">Loading…</p></div>;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <button data-testid="back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-stone-500 hover:text-emerald-800"><ArrowLeft className="w-4 h-4" /> Back to grove</button>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-700">{tree.tree_type} · Planted {tree.planting_date}</span>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-emerald-950">{tree.name}</h1>
            <p className="text-stone-500">{tree.pot_size} · {tree.soil_type} soil · {tree.stature}</p>
          </div>
          <Button data-testid="delete-tree-btn" onClick={remove} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
        </div>

        {/* Species Field Card */}
        {tree.field_card && (
          <motion.div data-testid="species-field-card" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6 grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-black/5 bg-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
            <div className="bg-emerald-950 text-white p-6">
              <Sprout className="w-5 h-5 text-amber-300" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-emerald-200">Species</p>
              <p className="font-serif text-2xl">{tree.field_card.commonName}</p>
              {tree.field_card.category && <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.15em] bg-white/10 text-amber-200 px-2 py-0.5 rounded-full">{tree.field_card.category}</span>}
            </div>
            <div className="bg-white p-6">
              <Mountain className="w-5 h-5 text-emerald-700" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-400">Soil Preference</p>
              <p className="text-sm font-medium text-emerald-950 leading-snug">{tree.field_card.soilPreference}</p>
            </div>
            <div className="bg-white p-6">
              <ShieldAlert className="w-5 h-5 text-emerald-700" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-400">Spray Rotation</p>
              <p className="font-serif text-2xl text-emerald-950">Every {tree.field_card.sprayRotationWeeks} wks</p>
            </div>
          </motion.div>
        )}

        {tree.stature_warning && (
          <div data-testid="stature-warning" className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <p className="text-sm leading-relaxed">{tree.stature_warning}</p>
          </div>
        )}

        <DashboardBanners tree={tree} onChange={load} />

        <FruitProfile treeId={id} initial={tree.fruit_profile} locked={preview ? !hasFruitProfile(user.plan) : tree.fruit_profile_locked} onSaved={load} />

        {/* Timeline hero */}
        <div className="mt-8 rounded-3xl border border-black/5 bg-white p-6 lg:p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <h2 className="font-serif text-2xl text-emerald-950 mb-6">Care Calendar</h2>
          <Timeline milestones={tree.milestones} onSelect={setSelected} />
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Selected milestone + watering */}
          <div className="lg:col-span-2 space-y-6">
            {selected && (
              <motion.div key={selected.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-stone-400">{selected.phase}</span>
                  <Badge className={`rounded-full ${selected.status === "done" ? "bg-emerald-100 text-emerald-700" : selected.status === "active" ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-500"}`}>{selected.status}</Badge>
                </div>
                <h3 className="mt-2 font-serif text-3xl text-emerald-950">{selected.title}</h3>
                <p className="mt-3 text-stone-600">{selected.description}</p>
                {selected.checklist?.length > 0 && (
                  <div className="mt-5 space-y-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-400">Crew checklist</p>
                    {selected.checklist.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-stone-700"><Check className="w-4 h-4 text-emerald-600" /> {c}</div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            <div className="rounded-2xl border border-black/5 bg-emerald-950 text-white p-8">
              <Droplets className="w-7 h-7 text-amber-300" />
              <h3 className="mt-3 font-serif text-2xl">Smart watering</h3>
              <p className="mt-2 text-emerald-50/80">{tree.watering}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
                <span className="text-xs uppercase tracking-[0.2em] text-emerald-100/60">Climate zone</span>
                {["Hot/Arid", "Temperate", "Cool/Humid"].map((z) => (
                  <button
                    key={z}
                    data-testid={`climate-toggle-${z}`}
                    onClick={() => setZone(z)}
                    className={`rounded-full px-3 py-1 text-xs transition-colors ${tree.climate_zone === z ? "bg-amber-300 text-emerald-950 font-semibold" : "bg-white/10 text-emerald-50/80 hover:bg-white/20"}`}
                  >{z}</button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-emerald-100/60">Units</span>
                {[["metric", "Metric (L · cm)"], ["imperial", "US (gal · in)"]].map(([u, label]) => (
                  <button
                    key={u}
                    data-testid={`units-toggle-${u}`}
                    onClick={() => setUnits(u)}
                    className={`rounded-full px-3 py-1 text-xs transition-colors ${(tree.units || "metric") === u ? "bg-amber-300 text-emerald-950 font-semibold" : "bg-white/10 text-emerald-50/80 hover:bg-white/20"}`}
                  >{label}</button>
                ))}
              </div>
              <div className="mt-4 border-t border-white/10 pt-4">
                {rain?.skip_watering && (
                  <div data-testid="rain-skip-banner" className="flex items-start gap-2 rounded-xl bg-amber-300/20 border border-amber-300/40 p-3 text-amber-100">
                    <CloudRain className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                    <p className="text-sm">Skip the next soak — ~{rain.precip_display ?? `${rain.precipitation_mm}mm`} rain forecast in the next {rain.horizon_hours}h (threshold {rain.threshold_display ?? `${rain.threshold_mm}mm`}).</p>
                  </div>
                )}
                {rain?.has_location && !rain.skip_watering && (
                  <p data-testid="rain-ok" className="text-sm text-emerald-100/70">{rain.weather_unavailable ? "\u2601\uFE0F Live weather is temporarily unavailable — water as scheduled." : `\u2600\uFE0F Only ~${rain.precip_display ?? `${rain.precipitation_mm}mm`} rain forecast — water as scheduled.`}</p>
                )}
                <div className="mt-2 flex items-center gap-2 text-xs text-emerald-100/50">
                  <span>{tree.latitude != null ? `Location: ${tree.latitude}, ${tree.longitude}${tree.zip_code ? ` · ZIP ${tree.zip_code}` : ""}` : "No location set"}</span>
                  <button data-testid="edit-location-btn" onClick={() => setEditLoc((v) => !v)} className="underline hover:text-emerald-50">{editLoc ? "cancel" : "edit"}</button>
                </div>
                {editLoc && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input data-testid="input-lat" value={loc.latitude} onChange={(e) => setLoc({ ...loc, latitude: e.target.value })} placeholder="lat" className="w-28 rounded-lg bg-white/10 px-2 py-1 text-sm text-white placeholder:text-emerald-100/40" />
                    <input data-testid="input-lon" value={loc.longitude} onChange={(e) => setLoc({ ...loc, longitude: e.target.value })} placeholder="lon" className="w-28 rounded-lg bg-white/10 px-2 py-1 text-sm text-white placeholder:text-emerald-100/40" />
                    <button data-testid="save-location-btn" onClick={saveLoc} className="rounded-full bg-amber-300 px-3 py-1 text-xs font-semibold text-emerald-950">Save</button>
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-emerald-100/60 border-t border-white/10 pt-3">{tree.care}</p>
            </div>

            {tree.irrigation?.length > 0 && (
              <div data-testid="irrigation-program" className="rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <h3 className="font-serif text-2xl text-emerald-950">Smart Irrigation Program</h3>
                <p className="text-xs text-stone-400 mt-1">{tree.soil_type} soil profile</p>
                <div className="mt-4 space-y-3">
                  {tree.irrigation.map((p, i) => (
                    <div key={i} className="rounded-xl border border-stone-100 bg-stone-50/60 p-4">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <span className="text-sm font-semibold text-emerald-950">{p.phase}</span>
                        <span className="font-mono-ag text-xs text-amber-700">{p.schedule}</span>
                      </div>
                      <p className="text-sm text-stone-600 mt-1">{p.details}</p>
                    </div>
                  ))}
                </div>
                {tree.spray_rotation && <p className="mt-4 text-sm text-stone-600 border-t border-stone-100 pt-3"><span className="font-semibold text-stone-800">Spray rotation — </span>{tree.spray_rotation}</p>}
              </div>
            )}

            {tree.guild?.length > 0 && (
              <div data-testid="companion-guild" className="rounded-2xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                <h3 className="font-serif text-2xl text-emerald-950">Companion Planting Guild</h3>
                <p className="text-xs text-stone-400 mt-1">Zoned around the root flare</p>
                <div className="mt-4 space-y-3">
                  {tree.guild.map((z, i) => (
                    <div key={i} className="flex gap-4 rounded-xl border border-stone-100 p-4">
                      <div className="shrink-0 w-16">
                        <div className="font-serif text-xl text-emerald-800">{z.zone.replace("Zone ", "Z")}</div>
                        <div className="font-mono-ag text-[10px] text-stone-400">{z.range}</div>
                      </div>
                      <div className="flex-1 border-l border-stone-100 pl-4">
                        <p className="text-sm font-semibold text-emerald-950">{z.plants}</p>
                        <p className="text-xs text-stone-500 mt-1">{z.purpose}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pests */}
            <div>
              <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Leaf className="w-5 h-5 text-emerald-700" /> Pest watch for {tree.tree_type}</h3>
              <div className="mt-4 grid gap-6">
                {tree.pests.length === 0 && <p className="text-sm text-stone-400">No common pests logged for this species.</p>}
                {tree.pests.map((p) => <PestCard key={p.name} pest={p} />)}
              </div>
            </div>

            {/* Diseases & Pathogens */}
            <div data-testid="disease-watch">
              <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-600" /> Disease &amp; Pathogen watch for {tree.tree_type}</h3>
              <div className="mt-4 grid gap-6">
                {(!tree.diseases || tree.diseases.length === 0) && <p className="text-sm text-stone-400">No diseases logged for this species.</p>}
                {tree.diseases && tree.diseases.map((d) => <DiseaseCard key={d.diseaseName} disease={d} />)}
              </div>
            </div>
          </div>

          {/* Photo upload */}
          <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)] h-fit">
            <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Camera className="w-5 h-5 text-emerald-700" /> Report a leaf issue</h3>
            <p className="text-xs text-stone-400 mt-1">Uploads route to your assigned landcare crew's job board.</p>
            <input ref={fileRef} data-testid="leaf-file-input" type="file" accept="image/*" onChange={onFile} className="mt-4 block w-full text-sm text-stone-500 file:mr-3 file:rounded-full file:border-0 file:bg-emerald-100 file:px-4 file:py-2 file:text-emerald-800" />
            {imgData && <img src={imgData} alt="preview" className="mt-3 rounded-xl h-40 w-full object-cover" />}
            <Textarea data-testid="leaf-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Describe what you're seeing…" className="mt-3 rounded-xl" />
            <Button data-testid="leaf-upload-btn" onClick={upload} disabled={busy} className="mt-3 w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white"><Upload className="w-4 h-4 mr-1" /> {busy ? "Sending…" : "Send to crew"}</Button>

            {tree.photos?.length > 0 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">Your reports & AI diagnosis</p>
                <div className="space-y-4">
                  {tree.photos.map((p) => {
                    const d = p.diagnosis;
                    const busyD = diagnosing.includes(p.id);
                    const catColor = { pest: "bg-amber-100 text-amber-700", disease: "bg-red-100 text-red-700", deficiency: "bg-orange-100 text-orange-700", environmental: "bg-sky-100 text-sky-700", healthy: "bg-emerald-100 text-emerald-700" }[d?.category] || "bg-stone-100 text-stone-600";
                    return (
                      <div key={p.id} data-testid={`leaf-report-${p.id}`} className="rounded-xl border border-stone-100 overflow-hidden">
                        <img src={photoSrc(p)} alt="leaf" className="h-40 w-full object-cover" />
                        <div className="p-3">
                          {p.note && <p className="text-xs italic text-stone-500 mb-2">"{p.note}"</p>}
                          {busyD ? (
                            <p data-testid={`diagnosis-loading-${p.id}`} className="flex items-center gap-2 text-sm text-emerald-700"><Sparkles className="h-4 w-4 animate-pulse" /> Analyzing leaf…</p>
                          ) : d ? (
                            <div data-testid={`diagnosis-${p.id}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${catColor}`}>{d.category}</span>
                                <span className="text-sm font-semibold text-emerald-950">{d.likely_issue}</span>
                                <span className="text-[10px] text-stone-400">{d.confidence} confidence</span>
                              </div>
                              <p className="mt-2 text-sm text-stone-600">{d.summary}</p>
                              {d.care_steps?.length > 0 && (
                                <ul className="mt-2 space-y-1">
                                  {d.care_steps.map((s, i) => (
                                    <li key={i} className="flex gap-2 text-xs text-stone-700"><Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />{s}</li>
                                  ))}
                                </ul>
                              )}
                              <button data-testid={`rediagnose-${p.id}`} onClick={() => runDiagnose(p.id)} className="mt-2 text-xs text-emerald-700 underline hover:text-emerald-900">Re-analyze</button>
                              {d.matched_pest && (
                                <button
                                  data-testid={`ref-link-${p.id}`}
                                  onClick={() => document.querySelector(`[data-testid="pest-card-${d.matched_pest}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                                  className="mt-2 ml-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 underline hover:text-amber-900"
                                >
                                  See {d.matched_pest} reference →
                                </button>
                              )}
                            </div>
                          ) : (
                            canAI ? (
                              <Button data-testid={`diagnose-btn-${p.id}`} size="sm" variant="outline" onClick={() => runDiagnose(p.id)} className="rounded-full w-full"><Sparkles className="mr-1 h-4 w-4" /> Diagnose with AI</Button>
                            ) : (
                              <Button data-testid={`diagnose-locked-${p.id}`} size="sm" variant="outline" onClick={() => navigate("/pricing")} className="rounded-full w-full border-amber-300 text-amber-700 hover:bg-amber-50"><Sparkles className="mr-1 h-4 w-4" /> Advanced AI — upgrade to Orchardist</Button>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
